import { useCallback, useRef, useState } from 'react';
import http from 'hub-http/clients/apiClient';
import { getEarlyRequesterResultPromise } from '../utils/getEarlyRequesterResultPromise';
import { correctRequestOptions } from '../utils/crmSearchRequestOptionsUtils';
import { useContentObjectType, useAuthInfoProvider } from 'content-management-data-lib';
import useContentGroups from './useContentGroups';
import { useQueryCacheForEmptyStateData } from './contentEmptyState';
import PortalIdParser from 'PortalIdParser';
import isEqual from 'hs-lodash/isEqual';
import LocalStorage from '../utils/LocalStorage';
import { CMP_LISTING_LIB_CACHE_FIRST_VIEW_OPTIMIZATION } from '../constants/debugConstants';
import enviro from 'enviro';
export default function useCrmFetcher({
  filterValidationConfig,
  hiddenFilter,
  emptyStateFilter,
  currentViewId,
  skipQuickfetchContentByViewOptimization,
  // We can fallback to '-2' - the "all" view should always have that id in the backend
  // because sales views standards suggests reusing ids for views:
  // https://docs.google.com/document/d/18k70spcoChJ91aA8-WH0WQB-eyXkJ3oex1y_GQq_-Og/edit?tab=t.0
  defaultViewId = '-2',
  hasViewsAccess
}) {
  // These two refs are used to optimize the first load of each content type view
  const shouldCacheViewRequestData = useRef(true);
  const shouldIgnoreEarlyRequestForContentData = useRef(false);
  const {
    contentObjectType,
    contentGroupObjectType
  } = useContentObjectType();
  const [searchTokens, setSearchTokens] = useState([]);
  const {
    userInfo,
    getIsUngatedToUseResolvedStateProperty
  } = useAuthInfoProvider();
  const {
    mergedContentGroupAssociations
  } = useContentGroups(contentGroupObjectType);
  const {
    queryCacheForEmptyStateData
  } = useQueryCacheForEmptyStateData({
    contentObjectType,
    contentGroupObjectType,
    emptyStateFilter
  });
  const isUngatedToUseResolvedState = getIsUngatedToUseResolvedStateProperty();
  const emptyStateFetcher = useCallback(async () => {
    const {
      data
    } = await queryCacheForEmptyStateData();
    return data.emptyStateData;
  }, [queryCacheForEmptyStateData]);
  const tableDataFetcher = useCallback(requestOptions => {
    const correctedRequestOptions = correctRequestOptions({
      requestOptions,
      userInfo,
      filterValidationConfig,
      mergedContentGroupAssociations,
      hiddenFilter,
      isUngatedToUseResolvedState
    });
    // This should only run once per load of each content type view. This is optimization logic to:
    //   - Use cached view request data if it exists and matches the current request data. We should ignore the cached data if:
    //       - the consumer has manually specified their quickfetch filters and opt to skip this via `skipQuickfetchContentByViewOptimization`
    //       - the consumer has no views access, so we can assume that we quickfetched with filters equivalent to the "all" view
    //   - If we have a cache miss, the cached filters don't match, or consumer has manually specificied their quickfetch filters, then:
    //       - cache this view's request data (first one on load) to speed up future loads
    //       - ignore quickfetch data from now on for this content type
    if (shouldCacheViewRequestData.current) {
      shouldCacheViewRequestData.current = false;
      if (!skipQuickfetchContentByViewOptimization && hasViewsAccess && currentViewId) {
        const storageKey = `CMP-listing-lib:${PortalIdParser.get()}:${contentObjectType}:first-view`;
        const cachedViewRequestData = LocalStorage.getAttributeParsed(storageKey);
        const cachedViewRequestDataMatchesCurrentRequestData = isEqual(cachedViewRequestData, correctedRequestOptions);
        const hasOptimisticallyQuickfetchedAllView = defaultViewId && currentViewId === defaultViewId && !cachedViewRequestData && !skipQuickfetchContentByViewOptimization && cachedViewRequestDataMatchesCurrentRequestData;
        if (!hasOptimisticallyQuickfetchedAllView && (!cachedViewRequestData || !cachedViewRequestDataMatchesCurrentRequestData || skipQuickfetchContentByViewOptimization)) {
          LocalStorage.setAttribute(storageKey, JSON.stringify(correctedRequestOptions));
          shouldIgnoreEarlyRequestForContentData.current = true;
        }
      }
    }
    const tableEarlyRequestName = shouldIgnoreEarlyRequestForContentData.current ? 'ignore-quickfetch--fetch-content-objects' : 'fetch-content-objects';
    if (enviro.debug(CMP_LISTING_LIB_CACHE_FIRST_VIEW_OPTIMIZATION)) {
      if (tableEarlyRequestName === 'fetch-content-objects') {
        console.log(`[DEBUG: ${CMP_LISTING_LIB_CACHE_FIRST_VIEW_OPTIMIZATION}] useCrmFetcher: using fetch-content-objects earlyRequest`);
      } else {
        console.log(`[DEBUG: ${CMP_LISTING_LIB_CACHE_FIRST_VIEW_OPTIMIZATION}] useCrmFetcher: ignoring quickfetch`);
      }
    }
    const request = getEarlyRequesterResultPromise({
      name: tableEarlyRequestName,
      retryRequest: () => http.post(`content-management/search/v1/${contentObjectType}`, {
        data: correctedRequestOptions
      })
    });
    return request.then(response => {
      setSearchTokens(response.queryTokens);
      return response;
    });
  }, [contentObjectType, currentViewId, defaultViewId, filterValidationConfig, hasViewsAccess, hiddenFilter, isUngatedToUseResolvedState, mergedContentGroupAssociations, skipQuickfetchContentByViewOptimization, userInfo]);
  const crmFetcher = useCallback(async requestOptions => {
    if (requestOptions.count === 1) {
      return await emptyStateFetcher();
    }
    return tableDataFetcher(requestOptions);
  }, [emptyStateFetcher, tableDataFetcher]);
  return {
    crmFetcher,
    searchTokens
  };
}